/**
 * Component shows summary information of each product in the cart
 *
 * @attr {String} [order-code] - Products are already ordered
 * @attr {Boolean} [read-only] - Simplified version
 * @attr {String} [css-class] - Additional css class
 *
 * @attr {String} [max-quantity-error-label] - Quantity selector max error text
 * @attr {String} [min-quantity-error-label] - Quantity selector min error text
 * @attr {String} [product-code-label] - product code (ref) label
 * @attr {String} [quantity-label] - product quantity-label
 * @attr {String} [gwp-label] - Title used to separate the gwp product rows
 * @attr {String} [giveaway-label] - Title used to separate the giveaway product rows
 *
 * Parameters - comes from redux store:
 * @param {Array} entries - The entries in the cart
 * @param {Array} giftProducts - The gift product entries in the cart
 *
 * Example of usage:
 * <e2-product-summary-grid
 *      read-only
 * ></e2-product-summary-grid>
 *
 * Order code is used on: payment, order-confirmation, order detail pages
 */

import { html } from 'lit-element';
import { ifDefined } from 'lit-html/directives/if-defined';
import { classMap } from 'lit-html/directives/class-map';

import { SimpleLitElement } from '@elab/components';
import connect from '@elab/core/store/connect';
import { actions, selectors } from '@elab/core/store/cart';
import { actions as globalActions, selectors as globalSelectors } from '@elab/core/store/global';
import { freezeProperty } from '@elab/core/utils/webComponent';
import { calculateMaxQuantity } from '@elab/core/utils/cart/CartUtils';
import { checkEgiftcard } from '@elab/core/utils/egiftcard';
import { button } from '@elab/core/utils/htmlTemplates';
import { getStrikeThroughPrice } from '@elab/core/utils/helpers';

import { stringToClassMapToken } from '../../../utils/webComponent';

export class ProductSummaryGrid extends SimpleLitElement {
    static get properties() {
        return {
            // From attributes
            orderCode: { type: String, attribute: 'order-code' },
            offlineOrder: { type: Boolean, attribute: 'offline-order' },
            forceBaseDiscountPrice: { type: Boolean, attribute: 'force-base-price' },
            readOnly: { type: Boolean, attribute: 'read-only' },
            cssClass: { type: String, attribute: 'css-class' },
            requestRemovalConfirmation: {
                type: Boolean,
                attribute: 'request-removal-confirmation',
            },
            productRowClasses: {
                type: String,
                attribute: 'product-row-classes',
            },
            productSummaryRowLayoutClasses: {
                type: String,
                attribute: 'product-summary-row-layout-classes',
            },

            // Labels:
            maxQuantityErrorLabel: {
                type: String,
                attribute: 'max-quantity-error-label',
            },
            minQuantityErrorLabel: {
                type: String,
                attribute: 'min-quantity-error-label',
            },
            productCodeLabel: { type: String, attribute: 'product-code-label' },
            quantityLabel: { type: String, attribute: 'quantity-label' },
            freeLabel: { type: String, attribute: 'free-label' },
            gwpLabel: { type: String, attribute: 'gwp-label' },
            giveawayLabel: { type: String, attribute: 'giveaway-label' },
            removeGtmLabel: { type: String, attribute: 'remove-gtm-label' },
            editGtmType: { type: String, attribute: 'edit-gtm-type' },
            giftProductsLabel: {
                type: String,
                attribute: 'gift-products-label',
            },

            entries: { type: Array, attribute: false },
            giftProducts: { type: Array, attribute: false },
            showGiftCardExtraData: {
                type: Boolean,
                attribute: 'show-gift-card-extra-data',
            },
            giftCardResendAllowed: {
                type: Boolean,
                attribute: 'gift-card-resend-allowed',
            },
            giftCardSenderLabel: {
                type: String,
                attribute: 'gift-card-sender-label',
            },
            giftCardReceiverLabel: {
                type: String,
                attribute: 'gift-card-receiver-label',
            },
            giftCardReceiverLabelAlt: {
                type: String,
                attribute: 'gift-card-receiver-label-alt',
            },
            giftCardEmailLabel: {
                type: String,
                attribute: 'gift-card-email-label',
            },
            giftCardResendLabel: {
                type: String,
                attribute: 'gift-card-resend-label',
            },
            giftCardWaitingLabel: {
                type: String,
                attribute: 'gift-card-waiting-label',
            },
            analyticsConfig: { type: String, attribute: 'analytics-config' },
            showPromotionBadge: {
                type: Boolean,
                attribute: 'show-promo-badge',
            },
            showUnitPrice: {
                type: Boolean,
                attribute: 'show-unit-price',
            },
            unitPriceLabel: {
                type: String,
                attribute: 'unit-price-label',
            },
            gtmPageType: {
                type: String,
                attribute: 'gtm-page-type',
            },
            shouldRedirectToPdp: {
                type: Boolean,
                attribute: 'should-redirect-to-pdp',
            },
            omnibusFeatureSwitcher: { type: Boolean, attribute: 'omnibus-feature-switcher' },
            showLabel: {
                type: Boolean,
                attribute: 'show-label',
            },
            labelButtonInStore: {
                type: String,
                attribute: 'button-label-store',
            },
            labelButtonOffline: {
                type: String,
                attribute: 'button-label-offline',
            },
            labelButtonOutOfStock: {
                type: String,
                attribute: 'button-label-out-stock',
            },
            priceStyleClass: { type: String, attribute: 'price-style-color' },
        };
    }

    constructor() {
        super();

        this.entries = [];
        this.giftProducts = [];
        this.orderCode = '';
        this.offlineOrder = false;
        this.maxQuantityErrorLabel = '';
        this.minQuantityErrorLabel = '';
        this.productCodeLabel = '';
        this.quantityLabel = '';
        this.cssClass = '';
        this.productRowClasses = '';
        this.productSummaryRowLayoutClasses = '';
        this.gwpLabel = '';
        this.giveawayLabel = '';
        this.giftProductsLabel = '';
        this.editGtmType = '';
        this.removeGtmLabel = '';
        this.giftCardSenderLabel = '';
        this.giftCardReceiverLabel = '';
        this.giftCardEmailLabel = '';
        this.giftCardResendLabel = '';
        this.giftCardWaitingLabel = '';
        this.giftProductLabel = '';
        this.showGiftCardExtraData = false;
        this.giftCardResendAllowed = false;
        this.analyticsConfig = '';
        this.unitPriceLabel = '';
        this.showUnitPrice = false;
        this.showPromotionBadge = false;
        this.gtmPageType = '';
        this.shouldRedirectToPdp = false;
        this.omnibusFeatureSwitcher = false;
        this.forceBaseDiscountPrice = false;
        this.showLabel = false;
        this.priceColor = '';
    }

    firstUpdated() {
        super.firstUpdated();
        freezeProperty(this, 'readOnly');
        if (this.lastTimePageLoad && !this.orderCode) {
            this.dispatchEvent(new CustomEvent('load-cart'));
        } else if (this.orderCode) {
            this.dispatchEvent(new CustomEvent('load-order'));
        }
    }

    getRemoveButtonTemplate({ code }) {
        return html`
            <e2-remove-cart-entry
                .code="${code}"
                ?request-removal-confirmation="${this.requestRemovalConfirmation}"
                gtm-label="${this.removeGtmLabel}"
                gtm-page-type="${this.gtmPageType}"
            ></e2-remove-cart-entry>
        `;
    }

    getItemImage({ name = '', images }) {
        return html`
            <e2-product-image
                format="list"
                .images="${images}"
                .productName="${name}"
                classes="product-item__img"
            ></e2-product-image>
        `;
    }

    getQuantitySelector(entry) {
        const { product = {} } = entry;
        return html`
            <e2-quantity-selector
                class="product-item__selector"
                css-counter-class="quantity-counter--product-summary"
                css-class="quantity-selector--product-summary"
                selector-type="buttons"
                .entry="${entry}"
                min-value="${product.minOrderQuantity || 1}"
                max-value="${calculateMaxQuantity(entry)}"
                analytics-config="${ifDefined(this.analyticsConfig || undefined)}"
                gtm-type="${this.editGtmType}"
                gtm-page-type="${this.gtmPageType}"
                min-error-text="${this.minQuantityErrorLabel}"
                max-error-text="${this.maxQuantityErrorLabel}"
            ></e2-quantity-selector>
        `;
    }

    getProductCode({ code = '' }) {
        return html`
            <span class="product-item__label">${this.productCodeLabel}</span>
            ${code}
        `;
    }

    getQuantity({ quantity = 0 }) {
        return html`
            <span class="product-item__label">${this.quantityLabel}</span>
            ${quantity}
        `;
    }

    getPromoTitle(name, promotion, label) {
        return promotion
            ? html`
                  <div class="product-summary__${name}-title">
                      ${label}
                  </div>
              `
            : null;
    }

    priceClassMap(basePriceWithDiscount) {
        return classMap({
            price__current: true,
            'price__current--free': !basePriceWithDiscount.value,
        });
    }

    getPrice({
        product = {},
        basePriceWithDiscount = {},
        basePrice = {},
        totalPriceWithoutPromotions = {},
        totalPrice = {},
        totalPriceWithoutPromotionsWithoutGiftOptions = {},
        totalPriceWithoutGiftOptions = {},
    }) {
        let price = null;
        let oldPrice = null;
        let hideMarkDown = false;
        hideMarkDown = product.masterBrand && product.masterBrand.hidePromotionsAndMarkdownPrice;
        if (product.egiftCard || !product.price) {
            price = basePriceWithDiscount.formattedValue || basePrice.formattedValue;
            oldPrice = basePrice.formattedValue;
        } else {
            let strikeThroughPrice = product.price.formattedOldValue;
            if (this.omnibusFeatureSwitcher) {
                const priorPrice = product.otherPrices.find(price => price.priceSource === 'PRIOR');
                strikeThroughPrice = getStrikeThroughPrice(priorPrice, product.price);
            }
            price =
                totalPriceWithoutGiftOptions.formattedValue ||
                totalPrice.formattedValue ||
                product.price.formattedValue;
            oldPrice =
                totalPriceWithoutPromotionsWithoutGiftOptions.formattedValue ||
                totalPriceWithoutPromotions.formattedValue ||
                totalPrice.formattedValue ||
                strikeThroughPrice ||
                product.price.formattedValue;
        }

        if (this.forceBaseDiscountPrice) {
            price = basePriceWithDiscount.formattedValue;
        }
        return html`
            <div class="product-item__price price">
                ${price !== oldPrice && !hideMarkDown
                    ? html`
                          <div class="product-item__price__old">
                              ${oldPrice}
                          </div>
                      `
                    : null}
                <div
                    class="${this.priceClassMap(basePriceWithDiscount)}"
                    style="color: ${this.priceStyleClass}"
                >
                    ${this.offlineOrder ? '' : basePriceWithDiscount.value ? price : this.freeLabel}
                </div>
            </div>
        `;
    }

    getProductVariants({ variantValueCategories = [] }) {
        return html`
            <e2-product-variants
                variant-class="product-variant--product-summary"
                .variants="${variantValueCategories}"
            ></e2-product-variants>
        `;
    }

    getProductItem(name, data, url = '') {
        return html`
            <div
                @click=${ifDefined(this.shouldRedirectToPdp ? () => this.goToPdp(url) : undefined)}
                class="product-item__${name} ${data ? 'd-contents' : 'd-none'}"
                v-slot="${name}"
            >
                ${data}
            </div>
        `;
    }

    getMissedOffers(name, messages) {
        return html`
            <div v-slot="${name}" class="product-item__missed-offer-container">
                ${messages.map(
                    item => html`
                        <div class="product-item__missed-offer-message">
                            ${item.messages.map(
                                msg =>
                                    html`
                                        <div>${msg}</div>
                                    `,
                            )}
                        </div>
                        <div class="icon product-item__missed-offer-message__icon"></div>
                    `,
                )}
            </div>
        `;
    }

    getProductGiftWrap(entry) {
        return entry.giftOptionsTotal && entry.giftOptionsTotal.giftOptionTotals
            ? html`
                  ${entry.giftOptionsTotal.giftOptionTotals.map(giftOption => {
                      const hideFreeLabel =
                          giftOption.optionTotalPrice && giftOption.optionTotalPrice.value;
                      return giftOption.type == 'MESSAGE'
                          ? html`
                                <div class="product-row__gift-wrap--item">
                                    <span class="product-row__gift-wrap--left">
                                        <span class="product-row__gift-wrap-description">
                                            ${giftOption.giftOptionType}
                                        </span>
                                        <span class="product-row__gift-wrap-quantity">
                                            x${giftOption.quantity}
                                        </span>
                                    </span>

                                    <span class="product-row__gift-wrap--right valid--green"
                                        >&#10004;</span
                                    >
                                </div>
                            `
                          : html`
                                <div class="product-row__gift-wrap--item">
                                    <span
                                        class="product-row__gift-wrap--left product-row__gift-wrap-description"
                                        >${giftOption.giftOptionType}</span
                                    >
                                    <span
                                        class="product-row__gift-wrap--right ${hideFreeLabel
                                            ? ''
                                            : 'valid--green'}"
                                    >
                                        ${hideFreeLabel
                                            ? giftOption.optionTotalPrice.formattedValue
                                            : this.freeLabel}
                                    </span>
                                </div>
                            `;
                  })}
              `
            : html``;
    }

    getProductRowClasses() {
        return classMap({
            'product-summary__row': true,
            'product-item': true,
            ...(this.productRowClasses ? stringToClassMapToken(this.productRowClasses) : null),
        });
    }

    goToPdp(url) {
        if (url) {
            this.dispatchEvent(
                new CustomEvent('goto-pdp', {
                    detail: {
                        url,
                    },
                }),
            );
        }
    }

    getButtonLabelText(entry) {
        let label = this.labelButtonOffline;
        if (entry.product.stock) {
            if (entry.product.stock.stockLevelStatus === 'inStock') {
                label = this.labelButtonInStore;
            } else {
                label = this.labelButtonOutOfStock;
            }
        }
        return label;
    }

    getLabelButton(entry) {
        const btnLabel = this.getButtonLabelText(entry);
        const additionalClass = btnLabel === this.labelButtonOffline ? 'button--gray' : '';
        return html`
            <e2-button
                class-modifier="button--primary ${additionalClass}"
                size="${this.buttonsSize ? this.buttonsSize : 'small'}"
                @click="${() => this.handleBtnClick(entry)}"
                style="${this.fullWidthButton ? 'width: 100%' : ''}"
                ?no-arrow="${this.fullWidthButton || this.noArrow}"
                ?full-width="${this.fullWidthButton}"
            >
                ${btnLabel}
            </e2-button>
        `;
    }

    handleBtnClick(entry) {
        const label = this.getButtonLabelText(entry);
        const { product = {} } = entry;
        const { url = '' } = product;
        if (label !== 'IN-STORE-ONLY' && url) {
            this.goToPdp(url);
        }
    }

    getProductRow = entry => {
        const eGiftcard = checkEgiftcard(entry.configurationInfos || []);
        const { product = {}, giveAway, entryNumber } = entry;
        const { masterBrand = {}, rangeName = '', name, url = '' } = product;

        return html`
            ${this.getPromoTitle('giveaway', giveAway, this.giveawayLabel)}
            <e2-product-summary-row-layout
                css-class="${this.productSummaryRowLayoutClasses}"
                class="${this.getProductRowClasses()}"
                data-base-product-code="${product.baseProduct}"
                ?showLabel="${this.showLabel}"
            >
                <div
                    @click=${ifDefined(
                        this.shouldRedirectToPdp ? () => this.goToPdp(url) : undefined,
                    )}
                    v-slot="image"
                >
                    ${this.getItemImage(product)}
                </div>
                ${this.getProductItem('brand', masterBrand.name, url)}
                ${this.getProductItem('range', rangeName, url)}
                ${this.getProductItem('name', name, url)}
                ${entry.missedOfferMessages && entry.missedOfferMessages.length
                    ? this.getMissedOffers('missed-offer', entry.missedOfferMessages)
                    : this.getMissedOffers('missed-offer', [])}

                <div v-slot="variants">${this.getProductVariants(product)}</div>
                <div v-slot="unit-price">${this.getUnitPrice(entry)}</div>
                <div v-slot="quantity">${this.getQuantity(entry)}</div>
                <div v-slot="promotion">${this.getPromotionTemplate(entry)}</div>
                <div v-slot="product-code">${this.getProductCode(product)}</div>
                <div v-slot="gift-card-details">${this.getEGiftCardDetails(eGiftcard)}</div>

                <div v-slot="price">${this.getPrice(entry)}</div>
                <div v-slot="show-label" class="product-item__label-btn">
                    ${this.showLabel ? this.getLabelButton(entry) : null}
                </div>
                <div v-slot="quantity-selector">
                    ${this.readOnly ? '' : this.getQuantitySelector(entry)}
                </div>

                <div v-slot="gift-wrap">${this.getProductGiftWrap(entry)}</div>
                <div v-slot="delete-button">
                    ${this.readOnly ? '' : this.getRemoveButtonTemplate(product)}
                </div>

                <div v-slot="resend-button">
                    ${this.getEGiftCardButton(eGiftcard, entryNumber)}
                </div>
            </e2-product-summary-row-layout>
        `;
    };

    getPromotionTemplate({ appliedPromotions = [] }) {
        return this.showPromotionBadge
            ? html`
                  <div class="product-item__promotion-wrap">
                      ${appliedPromotions
                          .sort((a, b) => a.promoNumber - b.promoNumber)
                          .map(this.renderPromoBadge)}
                  </div>
              `
            : '';
    }

    renderPromoBadge = ({ message = '', amount = {} }) => html`
        <div class="product-item__promotion-row">
            <span class="product-item__promotion-badge">${message}</span>
            <span class="product-item__promotion-price">${amount.formattedValue}</span>
        </div>
    `;

    getUnitPrice(entry) {
        const promo =
            this.offlineOrder || (entry.appliedPromotions && entry.appliedPromotions.length > 0);
        return this.showUnitPrice
            ? html`
                  <span class="product-item__label"> ${this.unitPriceLabel} </span>
                  ${entry.basePrice
                      ? html`
                            <span
                                class="${`product-item__price-amount${promo ? '--striked' : ''}`}"
                            >
                                ${entry.basePrice.formattedValue}
                            </span>
                        `
                      : ''}
                  ${promo
                      ? html`
                            <span class="product-item__price-amount">
                                ${entry.basePriceWithDiscount
                                    ? entry.basePriceWithDiscount.formattedValue
                                    : ''}
                            </span>
                        `
                      : ''}
              `
            : '';
    }

    getGwpAndFreeSamplesProductRow = entry => {
        const { product = {} } = entry;
        const { masterBrand = {}, rangeName, name } = product;
        return html`
            <e2-product-summary-row-layout
                css-class="${this.productSummaryRowLayoutClasses}"
                class="${this.getProductRowClasses()}"
                data-base-product-code="${product.baseProduct}"
            >
                <div v-slot="image">${this.getItemImage(product)}</div>
                ${this.getProductItem('brand', masterBrand.name)}
                ${this.getProductItem('range', rangeName)} ${this.getProductItem('name', name)}
                <div v-slot="product-code">${this.getProductCode(product)}</div>
                <div v-slot="price">${this.getPrice(entry)}</div>
            </e2-product-summary-row-layout>
        `;
    };

    getGwpAndFreeSamples(gwpProducts = [], freeSamples = []) {
        const gwpEntriesAvailable = gwpProducts.length > 0;
        const freeSampleEntriesAvailable = freeSamples.length > 0;
        if (gwpEntriesAvailable) {
            return html`
                ${this.getPromoTitle('giveaway', true, this.gwpLabel)}
                ${gwpProducts.map(this.getGwpAndFreeSamplesProductRow)}
            `;
        }
        if (freeSampleEntriesAvailable) {
            return html`
                ${this.getPromoTitle('giveaway', true, this.giveawayLabel)}
                ${freeSamples.map(this.getGwpAndFreeSamplesProductRow)}
            `;
        }
    }

    getGwpAndFreeSampleProductRows() {
        const gwpProducts = this.giftProducts.filter(entry => entry.isGwp);
        const freeSamples = this.giftProducts.filter(entry => entry.freeSample);
        return html`
            ${this.getGwpAndFreeSamples(gwpProducts, [])}
            ${this.getGwpAndFreeSamples([], freeSamples)}
        `;
    }

    getGwpProductRow = entry => {
        const { product = {} } = entry;
        const { masterBrand = {}, rangeName, name } = product;

        return html`
            ${this.getPromoTitle('gwp', true, this.gwpLabel)}
            <e2-product-summary-row-layout
                css-class="gift-product-layout-row gift-product-row-cursor"
                class="${this.getProductRowClasses()} gift-product-row"
            >
                <div v-slot="image">${this.getItemImage(product)}</div>

                ${this.getProductItem('brand', masterBrand.name)}
                ${this.getProductItem('range', rangeName)} ${this.getProductItem('name', name)}
                <div v-slot="quantity">${this.getQuantity(entry)}</div>
                <div v-slot="product-code">${this.getProductCode(product)}</div>
                <div v-slot="price">${this.getPrice(entry)}</div>
            </e2-product-summary-row-layout>
        `;
    };

    getGiftProducts() {
        return html`
            <div class="gift-products">
                <div class="gift-products__title">
                    <div class="icon gift-products__icon"></div>
                    <div class="gift-products__text">${this.giftProductsLabel}</div>
                </div>
                ${this.giftProducts.map(this.getGwpProductRow)}
            </div>
        `;
    }

    getEGiftCardDetails(eGiftcard) {
        return this.showGiftCardExtraData && eGiftcard !== null
            ? html`
                  <div class="product_row__gift-card-info-row">
                      <span>${this.giftCardReceiverLabel}</span>
                      ${eGiftcard.recipientName}
                  </div>
                  <div class="product_row__gift-card-info-row">
                      <span>${this.giftCardEmailLabel}</span>
                      ${eGiftcard.recipientEmail}
                  </div>
              `
            : '';
    }

    getEGiftCardButton(eGiftcard, entryNumber) {
        return this.showGiftCardExtraData && eGiftcard !== null
            ? html`
                  ${button(
                      {
                          className:
                              'product-summary__giftcard-details-row button--primary button--normal',
                          disabled: !this.giftCardResendAllowed,
                          clickHandler: () => this.handleResendEGiftCardClick(entryNumber),
                      },
                      this.giftCardResendAllowed
                          ? this.giftCardResendLabel
                          : this.giftCardWaitingLabel,
                  )}
              `
            : '';
    }

    handleResendEGiftCardClick(entryNumber) {
        if (!this.giftCardResendAllowed) return;

        this.dispatchEvent(
            new CustomEvent('open-dialog', {
                detail: { name: 'egift-card-resend-modal' },
            }),
        );
        this.dispatchEvent(
            new CustomEvent('set-egift-card-entry-number', {
                detail: entryNumber,
            }),
        );
    }

    render() {
        return html`
            <div class="product-summary ${this.cssClass}">
                ${this.analyticsConfig && this.analyticsConfig !== ''
                    ? this.entries.map(entry => {
                          return html`
                              <e2-click-tracking
                                  analytics-config="${this.analyticsConfig}"
                                  type="account_orders-product"
                              >
                                  ${this.getProductRow(entry)}
                              </e2-click-tracking>
                          `;
                      })
                    : this.entries.map(this.getProductRow)}
                ${this.giftProducts.length > 0
                    ? this.readOnly
                        ? this.getGwpAndFreeSampleProductRows()
                        : this.getGiftProducts()
                    : ''}
            </div>
        `;
    }
}

const mapStateToProps = state => ({
    entries: selectors.getEntriesWithoutFreeItems(state) || [],
    giftProducts: selectors.getGiftProducts(state) || [],
    // This property shouldn't be in attributes (no need to re-render)
    lastTimePageLoad: globalSelectors.getLastTimePageLoad(state),
});

const mapDispatchToEvents = dispatch => ({
    'load-order': () => dispatch(actions.loadCart()),
    'load-cart': () => dispatch(actions.loadCart()),
    'goto-pdp': ({ detail }) => dispatch(globalActions.goto(detail.url)),
});

export default () =>
    customElements.define(
        'e2-product-summary-grid',
        connect(mapStateToProps, mapDispatchToEvents)(ProductSummaryGrid),
    );
